import { SimpleColumn } from "../types";
import { renderEmpty } from "./shared";
import { User } from "lucide-react";
import { formatDate } from "@/lib/utils/crm/time-entry-helpers";
import { formatCurrency } from "@/lib/utils/crm/status-helpers";
import type { QBCustomerMatchStatus, SupabaseContactMinimal } from "@/lib/types/quickbooks-unified";

/**
 * Normalized QuickBooks invoice fields the columns read off the matching row's
 * `qb` payload. Hand-rolled (QB API objects are not codegen) — kept to exactly
 * the fields these renderers access.
 */
interface QBInvoiceDisplay {
  id?: string | null;
  docNumber?: string | null;
  txnDate?: string | null;
  dueDate?: string | null;
  totalAmt?: number | null;
  balance?: number | null;
  customerRef?: { value?: string; name?: string };
  privateNote?: string | null;
  realmId?: string | null;
}

/** A QuickBooks invoice matching row as rendered by the admin entities table. */
interface QBInvoiceMatchRow {
  qb?: QBInvoiceDisplay;
  status?: QBCustomerMatchStatus | string;
  matches?: SupabaseContactMinimal[];
  candidates?: SupabaseContactMinimal[];
  company?: { companyName?: string };
}

const MATCH_STATUS_COLORS: Record<string, string> = {
  matched: "bg-green-100 text-green-800",
  unmatched: "bg-red-100 text-red-800",
  ambiguous: "bg-yellow-100 text-yellow-800",
};

export function createQBInvoiceColumns(_invoices: QBInvoiceMatchRow[]): SimpleColumn[] {
  const columns: SimpleColumn[] = [];

  columns.push({
    id: "docNumber",
    label: "Invoice",
    category: "basic",
    visible: true,
    priority: 1,
    width: 200,
    render: (_value: unknown, item: QBInvoiceMatchRow) => (
      <div className="min-w-0">
        <div className="font-mono text-primary font-medium text-sm">
          #{item.qb?.docNumber || "Unknown"}
        </div>
        <div className="text-sm text-gray-500">
          {item.qb?.customerRef?.name || "Unknown Customer"}
        </div>
      </div>
    ),
  });

  columns.push({
    id: "totalAmt",
    label: "Amount",
    category: "financial",
    visible: true,
    priority: 2,
    width: 120,
    render: (_value: unknown, item: QBInvoiceMatchRow) => {
      if (!item.qb?.totalAmt) return renderEmpty();
      return (
        <div className="font-mono text-green-700 font-medium">
          {formatCurrency(item.qb.totalAmt)}
        </div>
      );
    },
  });

  columns.push({
    id: "balance",
    label: "Balance",
    category: "financial",
    visible: true,
    priority: 3,
    width: 120,
    render: (_value: unknown, item: QBInvoiceMatchRow) => {
      if (!item.qb?.balance) return renderEmpty();
      const isPaid = item.qb.balance === 0;
      return (
        <div className={`font-mono font-medium ${isPaid ? "text-green-700" : "text-red-700"}`}>
          {formatCurrency(item.qb.balance)}
        </div>
      );
    },
  });

  columns.push({
    id: "customerRef",
    label: "Customer",
    category: "contact",
    visible: true,
    priority: 4,
    width: 200,
    render: (_value: unknown, item: QBInvoiceMatchRow) => {
      if (!item.qb?.customerRef?.name) return renderEmpty();
      return (
        <div className="min-w-0">
          <div className="font-medium text-card-foreground truncate">
            {item.qb.customerRef.name}
          </div>
          {item.qb.customerRef.value && (
            <div className="text-sm text-gray-500 font-mono">ID: {item.qb.customerRef.value}</div>
          )}
        </div>
      );
    },
  });

  columns.push({
    id: "txnDate",
    label: "Date",
    category: "dates",
    visible: true,
    priority: 5,
    width: 120,
    render: (_value: unknown, item: QBInvoiceMatchRow) => {
      if (!item.qb?.txnDate) return renderEmpty();
      return <div className="text-sm text-gray-600">{formatDate(item.qb.txnDate)}</div>;
    },
  });

  columns.push({
    id: "dueDate",
    label: "Due Date",
    category: "dates",
    visible: true,
    priority: 6,
    width: 120,
    render: (_value: unknown, item: QBInvoiceMatchRow) => {
      if (!item.qb?.dueDate) return renderEmpty();

      const dueDate = new Date(item.qb.dueDate);
      const today = new Date();
      const isOverdue = dueDate < today && (item.qb.balance ?? 0) > 0;

      return (
        <div className={`text-sm ${isOverdue ? "text-red-600 font-medium" : "text-gray-600"}`}>
          {formatDate(item.qb.dueDate)}
        </div>
      );
    },
  });

  columns.push({
    id: "status",
    label: "Match Status",
    category: "status",
    visible: true,
    priority: 7,
    width: 120,
    render: (_value: unknown, item: QBInvoiceMatchRow) => (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          (item.status && MATCH_STATUS_COLORS[item.status]) || "bg-card-100 text-gray-800"
        }`}
      >
        {item.status || "unknown"}
      </span>
    ),
  });

  columns.push({
    id: "matches",
    label: "Matches",
    category: "integration",
    visible: true,
    priority: 8,
    width: 120,
    render: (_value: unknown, item: QBInvoiceMatchRow) => {
      const matchCount = item.matches?.length || 0;
      if (matchCount === 0) return <span className="text-gray-400">No matches</span>;

      return (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-gray-400" />
          <span className="font-medium">{matchCount}</span>
          <span className="text-sm text-gray-500">{matchCount === 1 ? "match" : "matches"}</span>
        </div>
      );
    },
  });

  columns.push({
    id: "privateNote",
    label: "Notes",
    category: "basic",
    visible: false,
    priority: 9,
    width: 200,
    render: (_value: unknown, item: QBInvoiceMatchRow) => {
      if (!item.qb?.privateNote) return renderEmpty();
      return (
        <div className="text-sm text-gray-600 truncate" title={item.qb.privateNote}>
          {item.qb.privateNote}
        </div>
      );
    },
  });

  columns.push({
    id: "qbId",
    label: "QB ID",
    category: "integration",
    visible: false,
    priority: 10,
    width: 120,
    render: (_value: unknown, item: QBInvoiceMatchRow) => {
      if (!item.qb?.id) return renderEmpty();
      return <span className="font-mono text-gray-600 text-sm">{item.qb.id}</span>;
    },
  });

  columns.push({
    id: "realmId",
    label: "Company",
    category: "integration",
    visible: true,
    priority: 11,
    width: 150,
    render: (_value: unknown, item: QBInvoiceMatchRow) => {
      if (!item.qb?.realmId) return renderEmpty();
      return (
        <div className="min-w-0">
          <div className="font-medium text-card-foreground truncate">
            {item.company?.companyName || `Company ${item.qb.realmId.slice(-4)}`}
          </div>
          <div className="text-sm text-gray-500 font-mono">{item.qb.realmId}</div>
        </div>
      );
    },
  });

  columns.push({
    id: "candidates",
    label: "Candidates",
    category: "integration",
    visible: false,
    priority: 12,
    width: 120,
    render: (_value: unknown, item: QBInvoiceMatchRow) => {
      const candidateCount = item.candidates?.length || 0;
      if (candidateCount === 0) return renderEmpty();

      return (
        <span className="font-medium text-blue-600">
          {candidateCount} {candidateCount === 1 ? "candidate" : "candidates"}
        </span>
      );
    },
  });

  columns.push({
    id: "created_at",
    label: "Created",
    category: "dates",
    visible: false,
    priority: 13,
    width: 120,
    render: () => {
      return <div className="text-sm text-gray-600">Synced</div>;
    },
  });

  columns.push({
    id: "actions",
    label: "Actions",
    category: "actions",
    visible: true,
    priority: 99,
    width: 200,
    render: () => {
      return <div className="text-sm text-gray-400">Actions</div>;
    },
  });

  return columns.sort((a, b) => a.priority - b.priority);
}
